import React, { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import jsPDF from "jspdf";
import { toast } from "sonner";
import { X, Download, FileText, Loader2, QrCode } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * SupporterQRModal — modal che mostra un QR code generato al volo per il link
 * supporter (URL /supporter/join/{token}). Permette il download in:
 *  - PNG (immagine ad alta risoluzione, 1024×1024)
 *  - PDF A4 impaginato con logo FIFE, nome squadra, istruzioni e ref www.fantafife.com
 *
 * Props:
 *  - shareUrl: URL completo del link supporter (obbligatorio)
 *  - teamName: nome squadra da mostrare in cima
 *  - coachName: opzionale, nome fantallenatore
 *  - onClose: callback per chiudere il modal
 */
export default function SupporterQRModal({ shareUrl, teamName, coachName, onClose }) {
  const canvasRef = useRef(null);
  const [dataUrl, setDataUrl] = useState(null);
  const [busy, setBusy] = useState(null); // "png" | "pdf" | null

  // Genera il QR al mount / cambio URL
  useEffect(() => {
    if (!shareUrl || !canvasRef.current) return;
    QRCode.toCanvas(canvasRef.current, shareUrl, {
      width: 320,
      margin: 1,
      color: { dark: "#0f172a", light: "#ffffff" },
      errorCorrectionLevel: "H",
    }).catch(() => toast.error("Impossibile generare il QR code"));
    // Data URL ad alta risoluzione per download
    QRCode.toDataURL(shareUrl, {
      width: 1024,
      margin: 2,
      color: { dark: "#0f172a", light: "#ffffff" },
      errorCorrectionLevel: "H",
    }).then(setDataUrl).catch(() => {});
  }, [shareUrl]);

  const downloadPNG = () => {
    if (!dataUrl) return toast.error("QR non ancora pronto");
    setBusy("png");
    try {
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `fife-supporter-${(teamName || "squadra").toLowerCase().replace(/\s+/g, "-")}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success("QR scaricato");
    } catch (e) {
      toast.error("Download fallito");
    } finally { setBusy(null); }
  };

  const downloadPDF = async () => {
    if (!dataUrl) return toast.error("QR non ancora pronto");
    setBusy("pdf");
    try {
      // A4 in mm: 210 × 297
      const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
      const W = 210, H = 297;

      // Sfondo brand su tutta la pagina — un rettangolo blu poi bianco al centro
      pdf.setFillColor(59, 86, 224); // #3b56e0 brand
      pdf.rect(0, 0, W, 60, "F");
      pdf.setFillColor(249, 115, 22); // #f97316 accento arancio
      pdf.rect(0, 60, W, 4, "F");

      // Logo FIFE in cima — carico l'asset dal path pubblico. jsPDF accetta dataURL o base64.
      // Se il fetch fallisce, saltiamo il logo senza bloccare il PDF.
      try {
        const logoBlob = await fetch("/logo-fife-512.png").then((r) => r.blob());
        const logoDataUrl = await new Promise((res) => {
          const fr = new FileReader();
          fr.onload = () => res(fr.result);
          fr.readAsDataURL(logoBlob);
        });
        pdf.addImage(logoDataUrl, "PNG", (W - 30) / 2, 12, 30, 30);
      } catch { /* logo opzionale */ }

      // Titolo bianco sopra il logo (in realtà sotto per non sovrapporre)
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(22);
      pdf.text("FIFE", W / 2, 52, { align: "center" });
      pdf.setFontSize(9);
      pdf.setFont("helvetica", "normal");
      pdf.text("FEDERAZIONE ITALIANA FANTALEGHE ELITE", W / 2, 58, { align: "center" });

      // Titolo grande "DIVENTA MIO SUPPORTER"
      pdf.setTextColor(15, 23, 42); // slate-900
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(24);
      pdf.text("DIVENTA MIO SUPPORTER", W / 2, 85, { align: "center" });

      // Nome squadra
      if (teamName) {
        pdf.setFontSize(28);
        pdf.setTextColor(59, 86, 224);
        pdf.text(teamName.toUpperCase(), W / 2, 100, { align: "center" });
      }
      if (coachName) {
        pdf.setFontSize(11);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(100, 116, 139);
        pdf.text(`Fantallenatore: ${coachName}`, W / 2, 108, { align: "center" });
      }

      // QR code al centro pagina — 100×100 mm
      const qrSize = 100;
      const qrX = (W - qrSize) / 2;
      const qrY = 118;
      // Cornice arrotondata di sfondo
      pdf.setFillColor(255, 255, 255);
      pdf.setDrawColor(226, 232, 240); // slate-200
      pdf.setLineWidth(0.5);
      pdf.roundedRect(qrX - 5, qrY - 5, qrSize + 10, qrSize + 10, 3, 3, "FD");
      pdf.addImage(dataUrl, "PNG", qrX, qrY, qrSize, qrSize);

      // Istruzioni sotto il QR
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(14);
      pdf.setTextColor(15, 23, 42);
      pdf.text("Scansiona il QR con la fotocamera del telefono", W / 2, qrY + qrSize + 18, { align: "center" });

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(11);
      pdf.setTextColor(71, 85, 105); // slate-600
      pdf.text("Inserisci nome, cognome ed email:", W / 2, qrY + qrSize + 26, { align: "center" });
      pdf.text("diventerai parte della fanbase e mi darai una spinta nel Fantacampionato 2026/27!", W / 2, qrY + qrSize + 32, { align: "center" });

      // Footer con link fantafife.com
      pdf.setFillColor(15, 23, 42); // slate-900
      pdf.rect(0, H - 22, W, 22, "F");
      pdf.setTextColor(255, 255, 255);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(11);
      pdf.text("www.fantafife.com", W / 2, H - 12, { align: "center" });
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      pdf.setTextColor(148, 163, 184); // slate-400
      pdf.text("Il Fantacampionato di casa — FIFE 2026/27", W / 2, H - 6, { align: "center" });

      // Save
      const filename = `fife-supporter-${(teamName || "squadra").toLowerCase().replace(/\s+/g, "-")}.pdf`;
      pdf.save(filename);
      toast.success("PDF scaricato");
    } catch (e) {
      console.error(e);
      toast.error("Generazione PDF fallita");
    } finally { setBusy(null); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose}
         data-testid="supporter-qr-modal">
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md p-6 md:p-8 space-y-5"
           onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose}
                className="absolute top-4 right-4 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center transition-colors"
                aria-label="Chiudi"
                data-testid="supporter-qr-close">
          <X className="w-5 h-5" />
        </button>

        <div className="flex flex-col items-center text-center gap-2">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 font-black">
            <QrCode className="w-3.5 h-3.5" style={{ color: "#3b56e0" }} />
            Diventa mio supporter
          </div>
          <div className="display font-black text-xl md:text-2xl text-slate-900">
            {teamName || "La mia squadra"}
          </div>
          {coachName && (
            <div className="text-xs text-slate-500">Fantallenatore: {coachName}</div>
          )}
        </div>

        <div className="flex justify-center">
          <div className="rounded-2xl border-2 border-slate-200 bg-white p-3">
            <canvas ref={canvasRef} data-testid="supporter-qr-canvas" />
          </div>
        </div>

        <div className="text-center text-xs text-slate-500 px-4">
          Chi scansiona questo QR verrà portato al modulo di iscrizione.
          Inserisce nome, cognome ed email e diventa parte della fanbase.
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Button onClick={downloadPNG} disabled={busy || !dataUrl}
                  variant="outline" className="rounded-full h-11 border-slate-300"
                  data-testid="supporter-qr-download-png">
            {busy === "png" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            PNG
          </Button>
          <Button onClick={downloadPDF} disabled={busy || !dataUrl}
                  className="btn-brand rounded-full h-11"
                  data-testid="supporter-qr-download-pdf">
            {busy === "pdf" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
            PDF impaginato
          </Button>
        </div>
      </div>
    </div>
  );
}
